// login-store-adapter.service.ts
import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { takeUntil, tap } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { Customer } from '@nuso/orgs/types';
import * as fromRoot from './reducers';
import * as login from './actions/login.actions';
import { LoginStateService } from '@nuso/auth/data-access';

/**
 * This adapter syncs the new LoginStateService state with the legacy NgRx store
 * to support a gradual migration away from the store-based approach.
 *
 * Since this is a root-level service, it exists for the lifetime of the application.
 */
@Injectable({ providedIn: 'root' })
export class LoginStoreAdapterService {
  private store = inject<Store<fromRoot.State>>(Store);
  private loginStateService = inject(LoginStateService);

  private destroy$ = new Subject<boolean>();

  /**
   * Initialize the adapter by subscribing to login state changes
   * and dispatching corresponding actions to the NgRx store
   */
  init(): void {
    // Subscribe to session info updates (including initial token refresh)
    this.loginStateService.onReceiveSessionInfo
      .pipe(
        takeUntil(this.destroy$),
        tap(loginStatus => {
          // Map and dispatch for session info updates
          const ngrxState = this.mapLoginStatusToNgrxState(loginStatus);
          this.store.dispatch(new login.SetLoginStateAction(ngrxState));
        })
      )
      .subscribe();

    // Handle logout events
    this.loginStateService.onLogout
      .pipe(
        takeUntil(this.destroy$),
        tap(() => {
          this.store.dispatch(new login.LogoutAction());
        })
      )
      .subscribe();

    // Handle login attempt events
    this.loginStateService.onAttemptLogin
      .pipe(
        takeUntil(this.destroy$),
        tap(loginStatus => {
          // Only set username if available in your login status model
          const payload = {
            username: loginStatus.loginSession?.username || ''
          };
          this.store.dispatch(new login.AttemptLoginAction(payload));
        })
      )
      .subscribe();
  }

  /**
   * Convert from LoginStatus to the format expected by the NgRx store
   */
  private mapLoginStatusToNgrxState(loginStatus: any): any {
    if (!loginStatus.loginSession) {
      return {
        pending: loginStatus.pending,
        isLoggedIn: loginStatus.isLoggedIn
      };
    }

    return {
      id: loginStatus.loginSession.id,
      username: loginStatus.loginSession.email,
      name: loginStatus.loginSession.name,
      roles: loginStatus.loginSession.roles || [],
      permissions: loginStatus.loginSession.permissions || [],
      pending: loginStatus.pending,
      isLoggedIn: loginStatus.isLoggedIn,
      redirectUrl: loginStatus.redirectUrl,
      impersonatorId: loginStatus.loginSession.impersonatorId,
      current_customer_id: loginStatus.loginSession.current_customer_id,
      currentCustomer: loginStatus.loginSession.currentCustomer,
      resellerCustomer: loginStatus.loginSession.resellerCustomer,
    };
  }

  /**
   * Manual cleanup method that can be called if needed
   */
  destroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}